'use client'

import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { SessionProvider, signOut } from 'next-auth/react'
import { ThemeProvider } from 'next-themes'
import { useEffect, useState } from 'react'
import axios from 'axios'
import { SessionIdleWarning } from '@/components/SessionIdleWarning'
import { isSessionRefreshInProgress } from '@/lib/session-refresh-state'

const BASE_PATH = '/hosting-control-panel'
const API_URL = process.env.NEXT_PUBLIC_API_URL || 'http://localhost:3001/api/v1'

/** Window for counting concurrent 401s before forcing sign-out. */
const UNAUTHORIZED_WINDOW_MS = 30_000
/** Require this many backend 401s inside the window before signOut (first fires warning). */
const UNAUTHORIZED_SIGNOUT_THRESHOLD = 3

function isBackendApiRequest(url: unknown): boolean {
    if (typeof url !== 'string' || !url) return false
    // Absolute backend URL (normal app calls)
    if (url.startsWith(API_URL)) return true
    // Relative fallback if some callers use '/api/v1/...'
    if (url.startsWith('/api/v1/')) return true
    return false
}

/**
 * Setup global 401 handler.
 * First 401 in a window → session warning (extend dialog).
 * Only after several 401s in the same window → signOut.
 * Never signOut while an access-token refresh is in progress.
 */
function setupAxios401Handler() {
    let added = false
    let windowStartedAt = 0
    let unauthorizedCount = 0
    let signOutStarted = false
    return () => {
        if (added) return
        added = true
        axios.interceptors.response.use(
            (r) => r,
            async (err) => {
                const status = err?.response?.status
                const requestUrl = err?.config?.url as unknown
                // Only auto-signout for backend API auth failures.
                // Do not terminate session for unrelated/internal 401s.
                if (status === 401 && isBackendApiRequest(requestUrl)) {
                    if (isSessionRefreshInProgress() || signOutStarted) {
                        return Promise.reject(err)
                    }

                    const now = Date.now()
                    if (now - windowStartedAt > UNAUTHORIZED_WINDOW_MS) {
                        windowStartedAt = now
                        unauthorizedCount = 0
                    }
                    unauthorizedCount += 1

                    if (typeof window !== 'undefined' && unauthorizedCount === 1) {
                        window.dispatchEvent(new Event('cloudaxe-session-warning'))
                    }

                    if (unauthorizedCount >= UNAUTHORIZED_SIGNOUT_THRESHOLD) {
                        signOutStarted = true
                        try {
                            await signOut({
                                redirect: true,
                                callbackUrl: `${BASE_PATH}/auth/login?sessionExpired=1`,
                            })
                        } catch (signOutErr) {
                            console.error('[providers] signOut after repeated 401s failed:', signOutErr)
                            signOutStarted = false
                        }
                    }
                }
                return Promise.reject(err)
            }
        )
    }
}

const add401Handler = setupAxios401Handler()

export function Providers({ children }: { children: React.ReactNode }) {
    useEffect(() => {
        add401Handler()
    }, [])
    const [queryClient] = useState(
        () =>
            new QueryClient({
                defaultOptions: {
                    queries: {
                        staleTime: 60 * 1000, // 1 minute
                        refetchOnWindowFocus: false,
                    },
                },
            })
    )

    return (
        <SessionProvider basePath="/hosting-control-panel/api/auth" refetchInterval={60}>
            <QueryClientProvider client={queryClient}>
                <ThemeProvider
                    attribute="class"
                    forcedTheme="light"
                    disableTransitionOnChange
                >
                    <SessionIdleWarning />
                    {children}
                </ThemeProvider>
            </QueryClientProvider>
        </SessionProvider>
    )
}
